import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import SendIcon from '@mui/icons-material/Send';

const API_BASE = process.env.REACT_APP_API_URL || 'https://cad.highwayrangers.com/api';

function Donated() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    story: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.story.trim()) {
      setError('Please share your story before submitting.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/donation-stories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name || 'Anonymous',
          email: formData.email || null,
          story: formData.story,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit story');
      }

      setSubmitted(true);
    } catch (err) {
      setError('Unable to submit your story at this time. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #003366 0%, #001a33 100%)',
        py: 6,
      }}
    >
      <Container maxWidth="md">
        {/* Thank You Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <FavoriteIcon sx={{ fontSize: 60, color: '#ffc107', mb: 2 }} />
          <Typography
            variant="h3"
            sx={{
              color: '#fff',
              fontWeight: 700,
              mb: 2,
            }}
          >
            Thank You for Your Donation
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: 'rgba(255,255,255,0.8)',
              maxWidth: 600,
              mx: 'auto',
            }}
          >
            Your generosity helps keep Texas motorists safe on the road.
          </Typography>
        </Box>

        {/* Personal Message Card */}
        <Paper
          elevation={8}
          sx={{
            p: 4,
            borderRadius: 3,
            background: '#fff',
            mb: 4,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 4,
              alignItems: 'flex-start',
            }}
          >
            {/* Drake's Portrait */}
            <Box
              sx={{
                flexShrink: 0,
                textAlign: 'center',
              }}
            >
              <Box
                component="img"
                src="/drakeportrait.png"
                alt="Drake, Director of Texas Highway Rangers"
                sx={{
                  width: { xs: 150, md: 180 },
                  height: { xs: 150, md: 180 },
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '4px solid #003366',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                }}
              />
              <Typography
                variant="subtitle1"
                sx={{
                  mt: 2,
                  fontWeight: 700,
                  color: '#003366',
                }}
              >
                Drake
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary' }}
              >
                Director
              </Typography>
            </Box>

            {/* Message */}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h5"
                sx={{
                  color: '#003366',
                  fontWeight: 700,
                  mb: 3,
                  borderLeft: '4px solid #ffc107',
                  pl: 2,
                }}
              >
                A Personal Note
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: 'text.primary',
                  lineHeight: 1.9,
                  mb: 2,
                  fontStyle: 'italic',
                  fontSize: '1.05rem',
                }}
              >
                Thank you for your donation — it truly matters.
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  lineHeight: 1.9,
                  mb: 2,
                }}
              >
                I'm Drake, the Director of Texas Highway Rangers. We started this nonprofit because 
                too many people end up stranded and alone on Texas roads. Our mission is straightforward: 
                provide free, safe, reliable roadside assistance and treat every person with respect—whether 
                it's a flat tire, a dead battery, fuel, or help getting back on the road.
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  lineHeight: 1.9,
                  mb: 2,
                }}
              >
                Your support funds what makes our help possible: training and certification, safety 
                equipment, tools, and vehicle maintenance, so our Volunteer Rangers can respond 
                responsibly and protect the people we serve.
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  lineHeight: 1.9,
                  mb: 2,
                }}
              >
                Personally, service has always been part of my life. I'm an Eagle Scout, I previously 
                served as a Texas Highway Patrol 911 dispatcher, and today I work in IT and public safety. 
                Our Rangers share that same spirit; some have public safety or mechanical experience, 
                and others simply show up because they refuse to drive past someone who needs help.
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  lineHeight: 1.9,
                  mb: 3,
                }}
              >
                If you'd like, share your story below — how we helped, or why you chose to support us. 
                We'll make sure your message reaches the team. Encouragement fuels this mission.
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: '#003366',
                  fontWeight: 600,
                  fontStyle: 'italic',
                }}
              >
                — Drake, Director, Texas Highway Rangers
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Story Submission Form */}
        <Paper
          elevation={8}
          sx={{
            p: 4,
            borderRadius: 3,
            background: '#fff',
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: '#003366',
              fontWeight: 700,
              mb: 1,
            }}
          >
            Share Your Story
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              mb: 3,
            }}
          >
            Tell us how we helped you, or why you chose to support our mission. Your story will be 
            shared with our team and may inspire others.
          </Typography>

          <Divider sx={{ mb: 3 }} />

          {submitted ? (
            <Alert
              severity="success"
              sx={{
                '& .MuiAlert-message': {
                  fontSize: '1rem',
                },
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Thank you for sharing your story!
              </Typography>
              <Typography variant="body2">
                Your message has been sent to our team. Stories like yours keep our Rangers motivated 
                to continue serving Texas motorists.
              </Typography>
            </Alert>
          ) : (
            <Box component="form" onSubmit={handleSubmit}>
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              <TextField
                fullWidth
                label="Your Name (optional)"
                name="name"
                value={formData.name}
                onChange={handleChange}
                sx={{ mb: 2 }}
                placeholder="Anonymous if left blank"
              />

              <TextField
                fullWidth
                label="Email (optional)"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                sx={{ mb: 2 }}
                placeholder="If you'd like us to follow up"
              />

              <TextField
                fullWidth
                label="Your Story"
                name="story"
                multiline
                rows={5}
                value={formData.story}
                onChange={handleChange}
                required
                sx={{ mb: 3 }}
                placeholder="Share how Texas Highway Rangers helped you, or why you chose to support our mission..."
              />

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                sx={{
                  bgcolor: '#003366',
                  px: 4,
                  py: 1.5,
                  '&:hover': {
                    bgcolor: '#002244',
                  },
                }}
              >
                {submitting ? 'Sending...' : 'Share Your Story'}
              </Button>
            </Box>
          )}
        </Paper>

        {/* Return Home Link */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Button
            href="/"
            variant="outlined"
            sx={{
              borderColor: '#ffc107',
              color: '#ffc107',
              '&:hover': {
                borderColor: '#ffca28',
                bgcolor: 'rgba(255,193,7,0.1)',
              },
            }}
          >
            Return to Home
          </Button>
        </Box>
      </Container>
    </Box>
  );
}

export default Donated;
